import {
  OverlayRef,
  Overlay,
  OverlayConfig,
  PositionStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
  SimpleChanges,
  TemplateRef,
  viewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  NgControl,
  Validators,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  NG_VALIDATORS,
  Validator,
  AbstractControl
} from '@angular/forms';
import { isEqual, isNaN } from 'lodash-es';
import { fromEvent, switchMap, of, take, tap } from 'rxjs';
import {
  AsYouType,
  CountryCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';
import {
  Country,
  CountryPhoneConfig,
  PhoneNumberValidity
} from './phone-number.models';
import { positions } from './phone-number.constants';
import { PhoneNumberService } from './phone-number.service';
import { DEFAULT_PHONE_CONFIG } from './constants';

@Component({
  selector: 'phone-number-field',
  host: {
    class: 'phone-number-field',
    '[attr.aria-label]': 'ariaLabel() || null',
    '[attr.aria-required]': 'isRequired().toString()',
    '[attr.aria-disabled]': 'isDisabled().toString()',
    '[attr.aria-readonly]': 'readonly().toString()',
    '[attr.aria-invalid]': 'isInvalid().toString()',
    '[class.input-disabled]': 'isDisabled()',
    '[class.input-readonly]': 'readonly()'
  },
  standalone: true,
  template: `<div class="phone-number-field__container">
      <button
        type="button"
        class="phone-number-field-trigger"
        [disabled]="isDisabled() || readonly() || !selectedCountry()"
        #triggerRef
      >
        @if (isLoadingCountries()) {
          <span class="country-placeholder__image"></span>
          <span class="country-placeholder__code"></span>
        } @else {
          @if (selectedCountry(); as country) {
            <img
              [src]="country.flag"
              [alt]="country.name"
              class="phone-number-field-trigger__image"
            />
            <span class="phone-number-field-trigger__code">{{
              country.dial_code
            }}</span>
            @if (filteredCountries().length > 1) {
              <span class="phone-number-field-trigger__arrow">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8.758"
                  height="5.379"
                  viewBox="0 0 8.758 5.379"
                >
                  <path
                    d="M666.168,833.138l2.965-2.965,2.965,2.965"
                    transform="translate(-664.754 -829.173)"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                  />
                </svg>
              </span>
            }
          }
        }
      </button>
      <input
        type="text"
        class="phone-number-field-input input-field"
        [id]="id()"
        [attr.disabled]="disabled() || null"
        [attr.readonly]="
          readonly() || isLoadingCountries() || !selectedCountry() || null
        "
        [attr.placeholder]="placeholder() || null"
        [attr.value]="value() || ''"
        (keydown)="handleKeyDown($event)"
        (input)="handleInput($event)"
        (focus)="handleFocus()"
        (blur)="handleBlur()"
      />
    </div>
    <ng-template #panelTpl>
      <div
        class="countries-panel"
        [class]="additionalClass().join(' ') || ''"
        [style.width.px]="hostFormField ? hostFormField.clientWidth : 0"
      >
        <div class="countries-panel__search-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21.691"
            height="21.688"
            viewBox="0 0 21.691 21.688"
          >
            <path
              id="Tracé_2"
              data-name="Tracé 2"
              d="M21.489,20.509,15.889,15a8.857,8.857,0,0,0,2.367-6.02A9.054,9.054,0,0,0,9.131,0,9.053,9.053,0,0,0,.006,8.98a9.053,9.053,0,0,0,9.125,8.98,9.177,9.177,0,0,0,5.743-2L20.5,21.486a.71.71,0,0,0,.992,0,.684.684,0,0,0,0-.977ZM9.131,16.578A7.66,7.66,0,0,1,1.41,8.98a7.66,7.66,0,0,1,7.721-7.6,7.66,7.66,0,0,1,7.721,7.6A7.66,7.66,0,0,1,9.131,16.578Z"
              transform="translate(-0.006 0)"
              fill="#F57425"
            />
          </svg>
          <input
            type="text"
            placeholder="Rechercher"
            [formControl]="searchCountry"
          />
        </div>
        <ul class="country-option-list" role="listbox">
          @for (country of filteredCountries(); track $index) {
            <li
              class="country-option"
              [class.country-option--selected]="
                selectedCountry()?.code === country.code
              "
              (click)="selectOption(country)"
            >
              <img
                [src]="country.flag"
                [alt]="country.name"
                class="country-option__image"
              />
              <span class="country-option__name">{{ country.name }}</span>
              <span class="country-option__code">{{ country.dial_code }}</span>
            </li>
          }
        </ul>
      </div>
    </ng-template>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PhoneNumberComponent)
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => PhoneNumberComponent)
    }
  ]
})
export class PhoneNumberComponent
  implements
    OnChanges,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ControlValueAccessor,
    Validator
{
  protected _phoneNumberService = inject(PhoneNumberService);
  protected _platformId = inject(PLATFORM_ID);

  valueChange = output<string | null>();
  countryChange = output<Country>();
  validityChange = output<PhoneNumberValidity>();
  blur = output<void>();

  value = input<string | null | undefined>();
  placeholder = input<string | undefined | null>();
  uniqueId = input.required<string>();
  ariaLabel = input('');
  disabled = input(false);
  readonly = input(false);
  additionalClass = input<string[]>([]);
  panelClass = input<string[]>([]);
  defaultCountry = input<CountryCode>('CI');
  preferredCountries = input<Array<CountryCode>>([]);
  disableDefaultValidation = input(false);

  panelTemplate = viewChild<TemplateRef<unknown>>('panelTpl');

  triggerElement = viewChild<ElementRef<HTMLButtonElement>>('triggerRef');

  searchCountry = new FormControl<string>('');

  panelOpen = signal(false);
  id = signal('');
  isInvalid = signal(false);
  isDisabled = signal(false);
  isRequired = signal(false);
  canStillWritting = signal(true);
  triggerLabel = signal<string | undefined>(undefined);
  countries: WritableSignal<Array<Country>> = signal([]);
  selectedCountry: WritableSignal<Country | undefined> = signal(undefined);
  filteredCountries: WritableSignal<Array<Country>> = signal([]);
  isLoadingCountries = signal(false);

  onChange: (value: unknown) => void = () => {};

  onTouched: () => void = () => {};

  private _panelPortal: TemplatePortal<unknown> | undefined;

  private _overlayRef: OverlayRef | undefined;

  get host(): HTMLElement {
    return this._vcr.element.nativeElement as HTMLElement;
  }
  private get _currentCountryConfig(): CountryPhoneConfig {
    return DEFAULT_PHONE_CONFIG;
  }

  get textField(): HTMLInputElement {
    return this.host.querySelector(
      '.phone-number-field-input'
    ) as HTMLInputElement;
  }

  get hostFormField(): HTMLElement | null {
    return this.host.closest('.form-field') as HTMLElement | null;
  }

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  private get _inputEvent(): InputEvent {
    const event = new InputEvent('input', { cancelable: true, bubbles: false });
    return event;
  }

  private get _ngControl(): NgControl | null {
    const ngControl = this._injector.get(NgControl, undefined, {
      optional: true,
      self: true
    });

    return ngControl;
  }

  constructor(
    private _overlay: Overlay,
    private _destroyRef: DestroyRef,
    private _vcr: ViewContainerRef,
    private _injector: Injector
  ) {
    this._getPreferredCountries();
  }

  ngOnInit(): void {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
      this.isRequired.set(
        this._ngControl.control?.hasValidator(Validators.required) ?? false
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this.id.set(this.uniqueId());
      }
    }

    if ('value' in changes) {
      this._setValue(this.value() || '');
    }

    if ('defaultCountry' in changes) {
      this._getDefaultCountry();
    }
  }

  ngAfterViewInit(): void {
    if (!this.hostFormField) {
      throw new Error(
        'To ensure proper functionality, SelectComponent should be hosted within a FormFieldComponent.'
      );
    } else {
      (
        this.hostFormField.querySelector('.form-field__wrapper') as HTMLElement
      ).style.padding = '6px 15px 6px 6px';
    }

    this._panelPortal = new TemplatePortal(this.panelTemplate()!, this._vcr);
    this._handleTriggerClick();
    this._handleSearchControl();
  }

  ngOnDestroy(): void {
    this._closePanel();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  writeValue(value: unknown): void {
    this._setValue((value || '') as string);
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    isDisabled
      ? this._addClassToHostElement('input-disabled')
      : this._removeClassFromHostElement('input-disabled');
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.disableDefaultValidation()) {
      return null;
    }

    const value = control.value;
    const countryCode = this.selectedCountry()?.code;

    if (!value) return null;

    const phoneNumber = parsePhoneNumberFromString(
      value,
      countryCode as CountryCode
    );

    if (!phoneNumber || !phoneNumber.isPossible()) {
      return { phoneNumber: true };
    }

    return null;
  }

  selectCountryCode(countryCode: string, focusAfterSelecting = true): void {
    const matchingOption = this.filteredCountries().find(
      el => el.code.toLowerCase() === countryCode.toLowerCase()
    );
    if (matchingOption) {
      this.selectOption(matchingOption, focusAfterSelecting);
    }
  }

  selectOption(option: Country, focusAfterSelecting = true): void {
    const oldValue = this.textField.value;
    this.selectedCountry.set(option);
    if (this.textField.value) {
      this.textField.value = '';
    }
    this.countryChange.emit(this.selectedCountry()!);
    this._closePanel();

    if (focusAfterSelecting) {
      this.textField.dispatchEvent(this._inputEvent);
      this.focusInput();
    } else if (oldValue) {
      this.onChange('');
    }
    this.filteredCountries.set(this.countries());
  }

  handleKeyDown(event: KeyboardEvent): void {
    const { key } = event;
    const ALLOW_KEYS = [
      'Shift',
      'Tab',
      'Backspace',
      'Enter',
      'Control',
      'v',
      'c',
      'ArrowLeft',
      'ArrowRight'
    ];
    const isDigit = !isNaN(Number(key)) && key !== ' ';

    const config = this._currentCountryConfig;

    if (config && isDigit) {
      const currentLength = this.textField.value.replace(/\D/g, '').length;
      if (currentLength >= config.maxDigits) {
        event.preventDefault();
        return;
      }
    }

    if (
      (!ALLOW_KEYS.includes(key) && !isDigit) ||
      key === 'e' ||
      (!this.canStillWritting() && !ALLOW_KEYS.includes(key))
    ) {
      event.preventDefault();
    }
  }

  handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;
    const country = this.selectedCountry();
    const config = this._currentCountryConfig;

    if (config) {
      const digits = value.replace(/\D/g, '');
      if (digits.length > config.maxDigits) {
        value = digits.substring(0, config.maxDigits);
      }
    }

    const asYouType = new AsYouType(country?.code as CountryCode);
    const newValue = asYouType.input(value);
    target.value = newValue;

    const currentDigits = value.replace(/\D/g, '').length;
    const isPossible = config
      ? currentDigits >= config.maxDigits
      : asYouType.isPossible();

    this.canStillWritting.set(!isPossible);

    newValue
      ? this._addClassToHostElement('input-filled')
      : this._removeClassFromHostElement('input-filled');

    const valueToEmit = newValue
      ? (asYouType.getNumber()?.number as string | undefined) || null
      : null;

    this.valueChange.emit(valueToEmit);
    this.onChange(valueToEmit || '');

    const phoneNumberValidity = {
      country: !!asYouType.country,
      phoneNumber: asYouType.isValid()
    };
    this.validityChange.emit(phoneNumberValidity);
  }

  handleFocus(): void {
    this._addClassToHostElement('input-focused');
  }

  handleBlur(): void {
    this.onTouched();
    this.blur.emit();
    this._removeClassFromHostElement('input-focused');
  }

  clearInput(): void {
    this.textField.value = '';
    this.textField.dispatchEvent(this._inputEvent);
    this.textField.focus();
  }

  focusInput(): void {
    this.textField.focus();
  }

  private _onOpenPanel(): void {
    if (this._overlayRef || !this.hostFormField) return;
    const origin = this.hostFormField.querySelector(
      '.form-field__wrapper'
    ) as HTMLElement;

    if (this.filteredCountries().length < 2) {
      return;
    }

    this._overlayRef = this._overlay.create(this._getOverlayConfig(origin));
    this._overlayRef.addPanelClass(this.panelClass());
    this._overlayRef.attach(this._panelPortal);
    this._handleOverlayBackdropClick();
    this.panelOpen.set(true);
    setTimeout(() => {
      this._scrollToSelectedOption();
    }, 50);
  }

  private _closePanel(): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
    this._overlayRef = undefined;
    this.panelOpen.set(false);
  }

  private _handleTriggerClick(): void {
    fromEvent(this.triggerElement()!.nativeElement, 'click')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(() => {
          this._onOpenPanel();
          this._addClassToHostElement('input-focused');
          return of(undefined);
        })
      )
      .subscribe();
  }

  private _handleOverlayBackdropClick(): void {
    this._overlayRef
      ?.backdropClick()
      .pipe(
        take(1),
        tap({
          next: () => {
            this._closePanel();
          }
        })
      )
      .subscribe();
  }

  private _getOverlayConfig(
    origin: HTMLElement,
    width?: number
  ): OverlayConfig {
    return new OverlayConfig({
      width,
      hasBackdrop: true,
      backdropClass: 'select-panel-backdrop',
      positionStrategy: this._getOverlayPosition(origin),
      scrollStrategy: this._overlay.scrollStrategies.reposition({
        autoClose: true
      })
    });
  }

  private _getOverlayPosition(origin: HTMLElement): PositionStrategy {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(positions)
      .withPush(false);

    return positionStrategy;
  }

  private _addClassToHostElement(value: string): void {
    this.host.classList.add(value);
  }

  private _removeClassFromHostElement(value: string): void {
    this.host.classList.remove(value);
  }

  private _scrollToSelectedOption(): void {
    const option = this.host.querySelector(
      '.country-option--selected'
    ) as HTMLElement | null;
    if (!option) return;

    const optionHeight = option.offsetHeight;
    const optionTop = option.offsetTop - 26;
    const optionParent = option.closest('.country-option-list') as HTMLElement;
    const optionParentHeight = optionParent.clientHeight;
    const distance = optionTop + optionHeight - optionParentHeight;

    if (distance > 0) {
      optionParent.scrollTop = distance;
    }
  }

  private _handleSearchControl(): void {
    this.searchCountry.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(value => {
          const countries = value
            ? this.countries().filter(country => {
                return country.name.toLowerCase().includes(value.toLowerCase());
              })
            : this.countries();

          this.filteredCountries.set(countries);
        })
      )
      .subscribe();
  }

  private _setValue(value: string | undefined | null): void {
    if (!value) return;

    const phoneNumber = parsePhoneNumberFromString(value);
    if (!phoneNumber) return;

    let country = this.countries().find(c =>
      isEqual(c.code, phoneNumber.country)
    );

    if (!country && phoneNumber.countryCallingCode) {
      country = this.countries().find(
        c => c.dial_code.replace('+', '') === phoneNumber.countryCallingCode
      );
    }

    if (!country) return;

    this.selectedCountry.set(country);
    this.textField.value = phoneNumber.nationalNumber || '';
    this.textField.dispatchEvent(this._inputEvent);
  }

  private _getPreferredCountries(): void {
    if (!this._isPlatformBrowser) return;
    this.isLoadingCountries.set(true);
    toObservable(this.preferredCountries)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        switchMap(preferredCountries => {
          return this._phoneNumberService.getCountries(preferredCountries);
        }),
        tap(preferredCountries => {
          if (!preferredCountries.length) {
            throw new Error(
              'To ensure proper functionality, the country list should not be empty.'
            );
          }
          this.countries.set(preferredCountries);
          this.filteredCountries.set(preferredCountries);
          this._getDefaultCountry();
          this.isLoadingCountries.set(false);

          const value = this.textField.value;
          if (!value) {
            this._setValue(this._ngControl?.value || this.value() || '');
          }
        })
      )
      .subscribe();
  }

  private _getDefaultCountry(): void {
    const countries = this.countries();
    const country =
      countries.find(c => isEqual(c.code, this.defaultCountry())) ||
      countries[0];

    this.selectOption(country, false);
  }
}
